/**
 * Luli For Babies — Lista de desejos em localStorage (réplica do wishlistStore
 * do site Lovable, que também era client-side via zustand/persist).
 *
 * Elementos:
 * - <luli-wishlist-toggle data-handle="..."> — botão coração (cards e PDP)
 * - <luli-wishlist-count> — contador no header
 * - <luli-wishlist-grid> — grade da página de lista de desejos
 */
const STORAGE_KEY = 'luli:wishlist';
const CHANGE_EVENT = 'luli:wishlist:change';

function readList() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string') : [];
  } catch (e) {
    return [];
  }
}

function writeList(handles) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
  } catch (e) {
    /* localStorage indisponível */
  }
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { count: handles.length } }));
}

function toggleHandle(handle) {
  const list = readList();
  const index = list.indexOf(handle);
  if (index >= 0) {
    list.splice(index, 1);
  } else {
    list.unshift(handle);
  }
  writeList(list);
  return index < 0;
}

function formatMoney(cents) {
  return 'R$ ' + (cents / 100).toFixed(2).replace('.', ',');
}

function escapeHtml(texto) {
  const div = document.createElement('div');
  div.textContent = texto == null ? '' : String(texto);
  return div.innerHTML;
}

/*
 * Adiciona um item ao carrinho e avisa o tema, para o drawer abrir e o contador
 * do topo subir. Fala a MESMA língua do product-form: um POST em
 * Theme.routes.cart_add_url e um evento `cart:update`. Quando esse evento chega
 * sem `sections`, o cart-items-component do tema refaz o próprio fetch e se
 * re-renderiza — então não precisamos montar o HTML do carrinho aqui.
 */
async function addToCart(variantId, button) {
  const rotulo = button.textContent;
  button.disabled = true;
  button.textContent = 'Adicionando…';

  const addUrl = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';

  try {
    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', '1');

    const res = await fetch(addUrl, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    });
    if (!res.ok) throw new Error('cart/add ' + res.status);

    // O contador do tema lê itemCount do evento; buscamos o total real do carrinho.
    const cart = await fetch('/cart.js').then((r) => r.json()).catch(() => null);

    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { data: { source: 'luli-wishlist', itemCount: cart ? cart.item_count : undefined } },
      })
    );

    button.textContent = 'Adicionado ✓';
    button.classList.add('luli-wishlist__add--done');
    setTimeout(() => {
      button.textContent = rotulo;
      button.classList.remove('luli-wishlist__add--done');
      button.disabled = false;
    }, 1600);
  } catch (e) {
    button.textContent = 'Tente de novo';
    setTimeout(() => {
      button.textContent = rotulo;
      button.disabled = false;
    }, 1600);
  }
}

/*
 * Produto com variação (tamanho/cor) NÃO deve ir para o carrinho no chute do
 * primeiro tamanho. Aqui abrimos o MESMO modal de escolha que a home usa.
 *
 * O truque: o tema renderiza um modal-casca único em toda página
 * (quick-add-modal.liquid via theme.liquid) — <quick-add-dialog> com um
 * #quick-add-modal-content dentro de um <dialog>. Buscamos a página do produto,
 * recortamos o [data-product-grid-content] (que traz <variant-picker> e o
 * <product-form-component>) e o injetamos nesse modal. Ao entrar no documento
 * vivo, os web components do tema se inicializam sozinhos; o product-form liga
 * seus eventos ao <dialog> ancestral (por isso a injeção TEM de ser ali, e não
 * num container nosso) e, ao adicionar, dispara o cart:update que o tema já
 * escuta — o modal fecha, o contador sobe e o drawer abre, tudo de graça.
 */
const quickAddCache = new Map();
let quickAddAbort = null;
document.addEventListener('cart:update', () => quickAddCache.clear());

async function openQuickAdd(productUrl, fallbackVariantId, button) {
  const dialog = document.getElementById('quick-add-dialog');
  const modalContent = document.getElementById('quick-add-modal-content');

  // Nada de modal vazio: se algo faltar, degrada para um caminho que funciona —
  // adiciona o primeiro tamanho disponível, ou manda para a página do produto.
  const degradar = (motivo) => {
    if (motivo) console.warn('[luli-wishlist] quick-add: ' + motivo);
    if (fallbackVariantId && button) {
      addToCart(fallbackVariantId, button);
    } else {
      window.location.href = productUrl;
    }
  };

  if (!dialog || typeof dialog.showDialog !== 'function' || !modalContent) {
    return degradar('modal do tema ausente');
  }

  // Busca com o caminho puro (mesma origem, sem query): um ?view= ou ?variant=
  // herdado da URL da wishlist vazaria para o fetch de variação do picker.
  let pathname;
  try {
    pathname = new URL(productUrl, location.origin).pathname;
  } catch (e) {
    return degradar('URL inválida');
  }

  try {
    let grid = quickAddCache.get(pathname);

    if (!grid) {
      quickAddAbort?.abort();
      quickAddAbort = new AbortController();
      const res = await fetch(pathname, { signal: quickAddAbort.signal });
      if (!res.ok) return degradar('fetch ' + res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const encontrado = doc.querySelector('[data-product-grid-content]');
      if (!encontrado) return degradar('grid do produto não encontrado');
      grid = encontrado.cloneNode(true);
      quickAddCache.set(pathname, grid);
    }

    // Clone descartável: a injeção move os nós, então o cache fica intacto.
    const fresh = grid.cloneNode(true);

    // No desktop o tema esconde o excesso por CSS (@media min-width:750px). No
    // mobile essa regra não vale, então replicamos a reorganização do tema:
    // fica só cabeçalho (título + preço), seletor de variação e botão de compra.
    if (!window.matchMedia('(min-width: 750px)').matches) {
      const productDetails = fresh.querySelector('.product-details');
      const productForm = fresh.querySelector('product-form-component');
      const variantPicker = fresh.querySelector('variant-picker');
      const productPrice = fresh.querySelector('product-price');

      const titulo = document.createElement('a');
      titulo.textContent = grid.querySelector('.view-product-title a')?.textContent?.trim() || '';
      titulo.href = productUrl;

      const header = document.createElement('div');
      header.classList.add('product-header');
      header.appendChild(titulo);
      if (productPrice) header.appendChild(productPrice);

      fresh.appendChild(header);
      if (variantPicker) fresh.appendChild(variantPicker);
      if (productForm) fresh.appendChild(productForm);
      productDetails?.remove();
    }

    // childrenOnly: joga os filhos do grid (mídia + detalhes) dentro do modal,
    // preservando o id/classe do #quick-add-modal-content. A inserção no
    // documento vivo é o que inicializa os web components clonados.
    modalContent.replaceChildren(...fresh.children);
    dialog.showDialog();
  } catch (e) {
    if (e && e.name === 'AbortError') return;
    return degradar('erro inesperado: ' + (e && e.message));
  }
}

class LuliWishlistToggle extends HTMLElement {
  connectedCallback() {
    this.handle = this.dataset.handle;
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      toggleHandle(this.handle);
      this.sync();
    });
    this.sync();
    document.addEventListener(CHANGE_EVENT, () => this.sync());
  }

  sync() {
    const active = readList().includes(this.handle);
    this.classList.toggle('luli-wishlist-active', active);
    this.button?.setAttribute('aria-pressed', active ? 'true' : 'false');
  }
}

class LuliWishlistCount extends HTMLElement {
  connectedCallback() {
    this.sync();
    document.addEventListener(CHANGE_EVENT, () => this.sync());

    /*
     * O tema re-renderiza o cabeçalho depois do carregamento: section-hydration
     * busca `?section_id=..._header_section` e aplica o resultado com morph.
     * O morph atualiza ESTE mesmo elemento no lugar — repõe o `hidden` e apaga
     * o texto que acabamos de escrever — e, como o elemento nunca é desconectado,
     * o connectedCallback não roda de novo e o número não voltava.
     *
     * Medido em produção: sync às 1749ms, morph apagando às 2193ms.
     *
     * Não há evento para escutar (sectionRenderer chama morph sem avisar
     * ninguém), então o contador se reescreve quando o DOM o contradiz. O
     * `sync` sai cedo quando já está correto, e é isso que impede o laço
     * infinito entre escrita e observador.
     */
    this.observer = new MutationObserver(() => this.sync());
    this.observer.observe(this, {
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['hidden'],
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  sync() {
    const count = readList().length;
    const texto = count > 0 ? String(count) : '';
    const escondido = count === 0;

    // Guarda do laço: sem isto, cada escrita dispararia o observador acima,
    // que chamaria sync de novo, para sempre.
    if (this.textContent === texto && this.hasAttribute('hidden') === escondido) return;

    this.textContent = texto;
    this.toggleAttribute('hidden', escondido);
  }
}

class LuliWishlistGrid extends HTMLElement {
  connectedCallback() {
    this.pixDiscount = parseFloat(this.dataset.pixDiscount || '5');
    this.render();

    // Muda por aqui (coração removido nesta página).
    this.onChange = () => this.render();
    document.addEventListener(CHANGE_EVENT, this.onChange);

    /*
     * Muda em OUTRA aba: favoritou num produto aberto noutra aba e voltou para
     * esta. O evento `storage` só dispara entre abas/janelas, então não há risco
     * de laço com o nosso próprio writeList (que usa o CHANGE_EVENT acima).
     */
    this.onStorage = (e) => {
      if (e.key === STORAGE_KEY) this.render();
    };
    window.addEventListener('storage', this.onStorage);

    /*
     * Re-render forçado pelo tema. No editor, ajustar qualquer configuração faz
     * o Shopify reinjetar o HTML do servidor desta seção — a lista, que é
     * montada aqui no cliente, voltaria vazia e o aviso de "lista vazia"
     * reapareceria mesmo com favoritos salvos. `shopify:section:load` é
     * disparado após essa reinjeção; re-renderamos quando é a nossa seção.
     */
    this.onSectionLoad = (e) => {
      if (this.isConnected && (!e.target || e.target.contains(this))) this.render();
    };
    document.addEventListener('shopify:section:load', this.onSectionLoad);
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.onChange);
    window.removeEventListener('storage', this.onStorage);
    document.removeEventListener('shopify:section:load', this.onSectionLoad);
  }

  async render() {
    const handles = readList();
    const empty = this.querySelector('[ref="empty"]');
    const list = this.querySelector('[ref="list"]');
    if (!list || !empty) return;

    if (handles.length === 0) {
      empty.hidden = false;
      list.innerHTML = '';
      return;
    }
    empty.hidden = true;

    /*
     * Cada render tem um token. Se um novo render começar enquanto os fetches
     * deste ainda estão no ar (ex.: o usuário remove um item durante o
     * carregamento), o render antigo desiste de escrever — sem isso, a resposta
     * atrasada sobrescreveria a lista já atualizada.
     */
    const token = (this.renderToken = (this.renderToken || 0) + 1);

    const products = (
      await Promise.all(
        handles.map((handle) =>
          fetch('/products/' + encodeURIComponent(handle) + '.js')
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null)
        )
      )
    ).filter(Boolean);

    if (token !== this.renderToken) return;

    list.innerHTML = products.map((product) => this.cardHtml(product)).join('');

    list.querySelectorAll('.luli-wishlist__remove').forEach((btn) => {
      btn.addEventListener('click', () => toggleHandle(btn.dataset.handle));
    });

    // Variante única: adiciona direto. Variação: abre o seletor (quick-add).
    list.querySelectorAll('.luli-wishlist__add[data-variant-id]').forEach((btn) => {
      btn.addEventListener('click', () => addToCart(btn.dataset.variantId, btn));
    });

    list.querySelectorAll('.luli-wishlist__add[data-quick-add]').forEach((btn) => {
      btn.addEventListener('click', () =>
        openQuickAdd(btn.dataset.productUrl, btn.dataset.fallbackVariantId, btn)
      );
    });
  }

  cardHtml(product) {
    const image = product.featured_image
      ? product.featured_image.replace(/(\.[a-z]{3,4})(\?|$)/i, '_400x$1$2')
      : '';
    const pix = Math.round(product.price * (1 - this.pixDiscount / 100));

    // variants.length > 1 é o espelho exato de has_only_default_variant no
    // Liquid: se for multi, a página do produto TEM um <variant-picker>; se não,
    // não tem. É o que garante que o quick-add nunca abre um modal sem seletor.
    const isMulti = Array.isArray(product.variants) && product.variants.length > 1;

    // Primeiro variante comprável — usado no add direto (variante única) e como
    // rede de segurança do quick-add se a página do produto não puder ser lida.
    const primeiroDisponivel = (product.variants || []).find((v) => v.available);

    let botaoCarrinho;
    if (isMulti) {
      // Variação: abre o seletor, como na home — nunca chuta o primeiro tamanho.
      // De propósito SEM fallback-variant: se a página do produto não puder ser
      // lida, o quick-add manda o cliente para a PDP escolher o tamanho, em vez
      // de adicionar o primeiro no chute (que é justamente o que evitamos aqui).
      botaoCarrinho =
        '<button type="button" class="luli-wishlist__add" data-quick-add data-product-url="' +
        escapeHtml(product.url) + '">Escolher opções</button>';
    } else if (primeiroDisponivel) {
      botaoCarrinho =
        '<button type="button" class="luli-wishlist__add" data-variant-id="' +
        primeiroDisponivel.id + '">Adicionar ao carrinho</button>';
    } else {
      botaoCarrinho = '<button type="button" class="luli-wishlist__add" disabled>Esgotado</button>';
    }

    return (
      '<div class="luli-recent__card luli-wishlist__card">' +
      '<a href="' + escapeHtml(product.url) + '" class="luli-wishlist__card-link">' +
      (image
        ? '<img class="luli-recent__image" loading="lazy" src="' + escapeHtml(image) + '" alt="">'
        : '<span class="luli-recent__image luli-recent__image--empty"></span>') +
      '<span class="luli-recent__title">' + escapeHtml(product.title) + '</span>' +
      '<span class="luli-recent__price">' + formatMoney(pix) + ' <small>no pix</small></span>' +
      '</a>' +
      botaoCarrinho +
      '<button type="button" class="luli-wishlist__remove" data-handle="' + escapeHtml(product.handle) + '">Remover</button>' +
      '</div>'
    );
  }
}

if (!customElements.get('luli-wishlist-toggle')) {
  customElements.define('luli-wishlist-toggle', LuliWishlistToggle);
}
if (!customElements.get('luli-wishlist-count')) {
  customElements.define('luli-wishlist-count', LuliWishlistCount);
}
if (!customElements.get('luli-wishlist-grid')) {
  customElements.define('luli-wishlist-grid', LuliWishlistGrid);
}
